<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowRight, Plus, RefreshCw } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useAuthStore } from '@yuksalish/auth'
import { useProcurementApi } from '../composables/useProcurementApi'
import { deliveryUrgency, isOpenPurchaseOrder, isOpenSupplierInvoice, purchaseOrderStatusClass } from '../types/procurement'
import type { PurchaseOrder, SupplierInvoice } from '../types/procurement'

const { t } = useI18n()
const api = useProcurementApi()
const auth = useAuthStore()

const orders = ref<PurchaseOrder[]>([])
const invoices = ref<SupplierInvoice[]>([])
const totalOrders = ref(0)
const loading = ref(true)
const error = ref('')
const today = new Date()

const canViewInvoices = computed(() => (auth.user?.permissions ?? []).includes('supplier_invoices.view'))

const openOrders = computed(() => orders.value.filter((order) => isOpenPurchaseOrder(order.status)))
const overdue = computed(() => openOrders.value.filter((order) => deliveryUrgency(order, today) === 'overdue'))
const received = computed(() => orders.value.filter((order) => order.status === 'received'))
const recent = computed(() => orders.value.slice(0, 8))
const openInvoices = computed(() => invoices.value.filter((invoice) => isOpenSupplierInvoice(invoice.status)))

const valueByCurrency = computed(() => {
  const totals = new Map<string, number>()

  for (const order of orders.value) {
    if (order.status === 'cancelled') continue
    const code = order.currency_code ?? '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(order.total_amount || 0))
  }

  return [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4)
})

function money(value: number, currency: string | null): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    // The register is paged server-side; the overview works on the newest page.
    const result = await api.listPurchaseOrders({ per_page: 100 })
    orders.value = result.items
    totalOrders.value = result.meta.total

    // Payables are optional: the viewer may not hold `supplier_invoices.view`.
    invoices.value = canViewInvoices.value ? await api.listSupplierInvoices().catch(() => []) : []
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-heading page-heading--actions">
    <div>
      <h2>{{ t('procurement.overview.title') }}</h2>
    </div>
    <RouterLink class="profile-action profile-action--primary" to="/purchase-orders/new">
      <Plus :size="15" aria-hidden="true" />
      {{ t('procurement.orders.newOrder') }}
    </RouterLink>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" type="button" @click="load">
      <RefreshCw :size="15" aria-hidden="true" />
      {{ t('procurement.common.retry') }}
    </button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-value">{{ totalOrders }}</span>
        <span class="summary-label">{{ t('procurement.overview.totalOrders') }}</span>
      </div>
      <div class="summary-card accent">
        <span class="summary-value">{{ openOrders.length }}</span>
        <span class="summary-label">{{ t('procurement.overview.openOrders') }}</span>
      </div>
      <div class="summary-card" :class="{ attention: overdue.length > 0 }">
        <span class="summary-value">{{ overdue.length }}</span>
        <span class="summary-label">{{ t('procurement.overview.overdue') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ received.length }}</span>
        <span class="summary-label">{{ t('procurement.overview.receivedOrders') }}</span>
      </div>
      <div v-if="canViewInvoices" class="summary-card" :class="{ accent: openInvoices.length === 0 }">
        <span class="summary-value">{{ openInvoices.length }}</span>
        <span class="summary-label">{{ t('procurement.overview.payables') }}</span>
      </div>
    </div>

    <div class="quick-grid">
      <RouterLink class="quick-card" to="/purchase-orders">
        <strong>{{ t('procurement.nav.purchaseOrders') }}</strong>
        <span>{{ t('procurement.overview.quickOrdersHint') }}</span>
      </RouterLink>
      <RouterLink class="quick-card" to="/deliveries">
        <strong>{{ t('procurement.nav.deliveries') }}</strong>
        <span>{{ t('procurement.overview.quickDeliveriesHint') }}</span>
      </RouterLink>
      <RouterLink class="quick-card" to="/suppliers">
        <strong>{{ t('procurement.nav.suppliers') }}</strong>
        <span>{{ t('procurement.overview.quickSuppliersHint') }}</span>
      </RouterLink>
      <RouterLink v-if="canViewInvoices" class="quick-card" to="/supplier-invoices">
        <strong>{{ t('procurement.nav.invoices') }}</strong>
        <span>{{ t('procurement.overview.quickInvoicesHint') }}</span>
      </RouterLink>
    </div>

    <div class="detail-grid">
      <div class="panel">
        <div class="panel-header">
          <h3>{{ t('procurement.overview.recent') }}</h3>
          <RouterLink class="link-action" to="/purchase-orders">
            {{ t('procurement.overview.viewAll') }}
            <ArrowRight :size="13" aria-hidden="true" />
          </RouterLink>
        </div>

        <table v-if="recent.length" class="data-table">
          <thead>
            <tr>
              <th>{{ t('procurement.orders.order') }}</th>
              <th>{{ t('procurement.orders.supplier') }}</th>
              <th>{{ t('procurement.orders.status') }}</th>
              <th class="cell-qty">{{ t('procurement.orders.total') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in recent" :key="order.id">
              <td class="cell-name">
                <RouterLink class="link-action" :to="`/purchase-orders/${order.id}`">#{{ order.id }}</RouterLink>
                <small class="cell-sub">{{ formatDisplayDate(order.created_at) }}</small>
              </td>
              <td>{{ order.supplier_name || '—' }}</td>
              <td>
                <span :class="['status-badge', purchaseOrderStatusClass(order.status)]">
                  {{ t(`procurement.status.${order.status}`) }}
                </span>
              </td>
              <td class="cell-qty">{{ money(order.total_amount, order.currency_code) }}</td>
            </tr>
          </tbody>
        </table>

        <p v-else class="empty-inline">{{ t('procurement.overview.empty') }}</p>
      </div>

      <div class="panel">
        <h3>{{ t('procurement.overview.valueByCurrency') }}</h3>
        <dl v-if="valueByCurrency.length" class="plain-list">
          <li v-for="[code, total] in valueByCurrency" :key="code">
            <span>{{ code }}</span>
            <strong>{{ money(total, null) }}</strong>
          </li>
        </dl>
        <p v-else class="empty-inline">{{ t('procurement.overview.empty') }}</p>
      </div>
    </div>
  </template>
</template>
